import React from 'react';
import styled from '@emotion/styled';
import { BLACK, GRAY, WHITE } from '../../../styles/constants/color';
import { fontFrame } from '../../../styles/mixin/font';
import { css } from '@emotion/react';

const Button = styled.button<{
  buttonWidth: string;
  buttonHeight: string;
  buttonFontWeight: string;
  buttonFontSize: string;
  buttonLineHeight: string;
  buttonColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  disabledColor: string;
  disabledBackgroundColor: string;
  disabledborderColor: string;
  buttonBorderSize: string;
  ButtonBorderRadius: string;
  needHover?: boolean;
}>`
  width: ${props => props.buttonWidth};
  height: ${props => props.buttonHeight};
  ${props =>
    fontFrame(
      props.buttonFontWeight,
      props.buttonFontSize,
      props.buttonLineHeight,
      props.buttonColor
    )};
  text-align: center;
  padding: 0 8px;
  border-radius: ${props => props.ButtonBorderRadius};
  border: ${props => `${props.buttonBorderSize} solid ${props.buttonBorderColor}`};
  background-color: ${props => props.buttonBackgroundColor};

  ${props =>
    props.needHover &&
    css`
      transition: all 0.2s ease-in-out;
      &:hover {
        transform: translateY(-2px);
        box-shadow: 2px 2px 3px 1px ${GRAY};
        -webkit-box-shadow: 2px 2px 3px 1px ${GRAY};
        -moz-box-shadow: 2px 2px 3px 1px ${GRAY};
      }
    `};

  &:disabled {
    color: ${props => props.disabledColor};
    border: 1px solid ${props => props.disabledborderColor};
    background-color: ${props => props.disabledBackgroundColor};
    cursor: pointer;
  }
`;

interface Props {
  text: string;
  width?: string;
  height?: string;
  onClick?: () => void;
  fontWeight?: string;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  disabledColor?: string;
  disabledBackgroundColor?: string;
  disabledborderColor?: string;
  borderSize?: string;
  borderRadius?: string;
  needHover?: boolean;
  disabled?: boolean;
}

/**
 *  공통 버튼 컴포넌트
 * @param {string} text button의 text
 * @param {string} width button의 width (default: auto)
 * @param {string} height button의 height (default: 30px)
 * @param {function} onClick onClick 이벤트
 * @param {string} fontWeight button의 fontWeight '400', Medium, Bold (default: 300)
 * @param {string} fontSize button의 fontSize (defalut: 14px)
 * @param {string} lineHeight button의 lineHeight (default: 1)
 * @param {string} color button의 color (default: #222)
 * @param {string} backgroundColor button의 backgroundColor (default: #fff)
 * @param {string} borderColor button의 borderColor (default: #d9dde2)
 * @param {string} disabledColor button이 disabled 상태일 때의 color (default: #fff)
 * @param {string} disabledBackgroundColor button이 disabled 상태일 때의 backgroundColor (default: #d9dde2)
 * @param {string} disabledBorderColor button이 disabled 상태일 때의 borderColor (default: #d9dde2)
 * @param {string} borderSize button의 border 두께 (default: 1px)
 * @param {string} borderRadius button의 borderRadius 수치 (default: 5px)
 * @param {boolean} needHover button의 needHover 기능 유무 (default: false)
 * @param {boolean} disabled disabled 여부 (default: false)
 *  */
function SimpleButton({
  text,
  width = 'auto',
  height = '30px',
  onClick,
  fontWeight = '400',
  fontSize = '14px',
  lineHeight = '1',
  color = BLACK,
  backgroundColor = WHITE,
  borderColor = GRAY,
  disabledColor = WHITE,
  disabledBackgroundColor = GRAY,
  disabledborderColor = GRAY,
  borderSize = '1px',
  borderRadius = '5px',
  needHover,
  disabled,
}: Props): JSX.Element {
  return (
    <Button
      onClick={() => onClick && onClick()}
      buttonWidth={width}
      buttonHeight={height}
      buttonFontWeight={fontWeight}
      buttonFontSize={fontSize}
      buttonLineHeight={lineHeight}
      buttonColor={color}
      buttonBackgroundColor={backgroundColor}
      buttonBorderColor={borderColor}
      disabledColor={disabledColor}
      disabledBackgroundColor={disabledBackgroundColor}
      disabledborderColor={disabledborderColor}
      buttonBorderSize={borderSize}
      ButtonBorderRadius={borderRadius}
      needHover={needHover}
      disabled={disabled}
    >
      {text}
    </Button>
  );
}

export default SimpleButton;
